import Card from "components/card";
import axiosClient from "axios-client";
import { useEffect, useState } from "react";
import ModalHorizon from "components/modal";
import Loading from "components/loading/Loading";
import LicenseChip from "components/LicenseChip";
import ErrorMessage from "components/ErrorMessage";
import BasicLayout from "components/wizard/BasicLayout";
import { useNavigate, useParams } from "react-router-dom";
import GridDetailsLayout from "components/GridDetailsLayout";
import ActivationBadgeType from "components/ActivationTypeBadge";
import { ACTIVATION_TYPES, MAINTENANCE_TYPES } from "utils/constants";

const ResellerOrderDetail = () => {
    const navigate = useNavigate();
    const { code: reseller_order_code } = useParams();

    const [load, setLoad] = useState(false);
    const [message, setMessage] = useState({});
    const [order, setOrder] = useState({});

    const [modal, setModal] = useState({
        active: false,
        title: null,
        btnVisible: false,
        onClickBtn: null,
        btnLabel: null,
    });

    const [modalConfirmed, setModalConfirmed] = useState(false);

    useEffect(() => {
        setLoad(true);

        axiosClient
            .get(`/reseller-order/${reseller_order_code}`)
            .then(({ data }) => {
                setOrder(data);
            })
            .catch((err) => {
                const response = err.response;

                if (response) {
                    setMessage({
                        text: response.data.message,
                        type: "ERROR",
                        errors: response.data.errors
                            ? response.data.errors
                            : {},
                    });
                }
            })
            .finally(() => {
                setLoad(false);
            });
    }, []);

    function activateOrder() {
        axiosClient
            .post(
                `/reseller-order/${reseller_order_code}/create-activation`,
                {}
            )
            .then(({ data }) => {
                if (data?.code) {
                    navigate(
                        `/my/activation-${order.type.toLowerCase()}/${
                            data?.code
                        }`
                    );
                }
            })
            .catch((err) => {
                const response = err.response;

                if (response) {
                    setMessage({
                        text: response.data.message,
                        type: "ERROR",
                        errors: response.data.errors
                            ? response.data.errors
                            : {},
                    });
                }
            });
    }

    function closeModal() {
        setModalConfirmed(false);
        setModal((previousState) => {
            return {
                ...previousState,
                active: false,
                title: null,
                btnVisible: null,
                onClickBtn: null,
                btnLabel: null,
            };
        });
    }

    return (
        <>
            <ModalHorizon
                title={modal.title}
                isOpen={modal.active}
                onClose={closeModal}
                btnVisible={modal.btnVisible}
                onClickBtn={modal.onClickBtn}
                btnLabel={modal.btnLabel}
                btnDisabled={!modalConfirmed}
                content={
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col gap-4 text-sm text-gray-700">
                            <p>
                                Confermi di voler procedere con la creazione di
                                una nuova attivazione per questo ordine?
                            </p>
                            <div className="info-box border !border-amber-300 bg-amber-50 !text-amber-800">
                                <p className="font-semibold">ATTENZIONE</p>
                                <p className="mt-1">
                                    Al completamento del processo di attivazione
                                    verrà{" "}
                                    <strong>
                                        {order?.type?.toLowerCase() ===
                                        ACTIVATION_TYPES.CLOUD
                                            ? "creato automaticamente un nuovo tenant"
                                            : "attivata automaticamente una nuova macchina"}
                                    </strong>{" "}
                                    {order?.type?.toLowerCase() ===
                                    ACTIVATION_TYPES.CLOUD
                                        ? "associato"
                                        : "associata"}{" "}
                                    a questo ordine.
                                    {order?.type?.toLowerCase() ===
                                        ACTIVATION_TYPES.CLOUD && (
                                        <>
                                            <br />
                                            La creazione del tenant è
                                            un'operazione{" "}
                                            <strong>irreversibile</strong>: in
                                            caso di inserimento errato sarà
                                            necessario richiederne disdetta e
                                            procedere con un nuovo ordine
                                            tramite il distributore.
                                        </>
                                    )}
                                </p>
                                <p className="mt-3">
                                    Se stai tentando di{" "}
                                    <strong>
                                        modificare le licenze di{" "}
                                        {order?.type?.toLowerCase() ===
                                        ACTIVATION_TYPES.CLOUD
                                            ? "un tenant"
                                            : "una macchina"}{" "}
                                        già esistente
                                    </strong>{" "}
                                    o di gestire licenze su{" "}
                                    {order?.type?.toLowerCase() ===
                                    ACTIVATION_TYPES.CLOUD
                                        ? "un tenant"
                                        : "una macchina"}{" "}
                                    differente, <strong>non procedere</strong> e
                                    contatta preventivamente l'Ufficio Ordini
                                    del distributore.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="confirm-activation"
                                className="h-4 w-4 cursor-pointer accent-brand-500"
                                checked={modalConfirmed}
                                onChange={(e) =>
                                    setModalConfirmed(e.target.checked)
                                }
                            />
                            <label
                                htmlFor="confirm-activation"
                                className="cursor-pointer select-none text-sm text-gray-700/90 transition hover:text-gray-800/90"
                            >
                                Confermo di voler procedere.
                            </label>
                        </div>
                    </div>
                }
            />

            {load && <Loading />}

            <Card extra="px-4 md:px-6 py-2 md:py-5 w-full h-full sm:overflow-x-auto gap-8">
                <BasicLayout
                    title={`Ordine ${order?.reseller_code}`}
                    backButtonRoute="/my/new-activation"
                    content={
                        <div className="flex flex-col items-center gap-10">
                            <GridDetailsLayout
                                dataConfig={[
                                    {
                                        key: "Tipologia attivazione",
                                        value: (
                                            <ActivationBadgeType
                                                text={order?.type}
                                                className="ml-1"
                                            />
                                        ),
                                    },
                                    {
                                        key: "Tipologia licenza",
                                        value: order?.subtype,
                                    },
                                    {
                                        key: "Licenze",
                                        value: (
                                            <div className="ml-1 flex flex-wrap gap-2">
                                                {order?.licenses &&
                                                    Object.values(
                                                        order.licenses
                                                    ).map(
                                                        ({
                                                            code,
                                                            name,
                                                            qty,
                                                            tooltip,
                                                        }) => (
                                                            <LicenseChip
                                                                key={code}
                                                                name={name}
                                                                quantity={qty}
                                                                tooltip={
                                                                    tooltip
                                                                }
                                                            />
                                                        )
                                                    )}
                                            </div>
                                        ),
                                    },
                                    {
                                        key: "Tipologia maintenance",
                                        value:
                                            MAINTENANCE_TYPES?.[
                                                order?.maintenance
                                            ] ?? order?.maintenance,
                                    },
                                    {
                                        key: "Dimensione server",
                                        value: order?.size,
                                    },
                                    {
                                        key: "Note",
                                        value: order?.notes,
                                    },
                                ]}
                            />

                            <ErrorMessage message={message} />

                            <button
                                onClick={() => {
                                    setModal({
                                        active: true,
                                        title: "Conferma attivazione",
                                        btnVisible: true,
                                        onClickBtn: () => {
                                            closeModal();
                                            activateOrder();
                                        },
                                        btnLabel: "Procedi",
                                    });
                                }}
                                className="w-fit rounded-[8px] bg-brand-500 px-[34px] py-[12px] text-base font-bold uppercase text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                            >
                                Attiva ora
                            </button>
                        </div>
                    }
                />
            </Card>
        </>
    );
};

export default ResellerOrderDetail;
